import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { AI_REEL_LENGTH_SEC } from 'fym-shared/src/music24/story/aiReelVideo';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { Panel, PanelHead } from '../../../components/primitives/Panel';
import { useMusic24Capabilities } from '../../../hooks/useMusic24Capabilities';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { reelMessages } from './reelMessages';
import { ReelUnavailableAlert } from './ReelUnavailableAlert';
import { WatermarkCallout } from '../../../watermarkUpsell/files/WatermarkCallout';
import { StudioCreditBlock } from '../../credits/StudioCreditBlock';
import { StudioStickyMakeBar } from '../../credits/StudioStickyMakeBar';
import { useIsNarrowCreditBar } from '../../credits/useIsNarrowCreditBar';
import { useVideoCreditGate } from '../../credits/useVideoCreditGate';
import { StudioActionFeedback } from '../studioActionErrorText';
import { StudioActionError } from '../StudioActionError';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole AI reel flow; each part uses its own slice of it */
import styles from './PromoteReels.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelRailProps {
  accountId: number;
  releaseTitle?: string;
  /** The theme of the song: the reel follows it. */
  themeId: ReleaseThemeId;
  /** The first frame of the reel drawn as the render draws it (design z6). Absent: the 9:16 stand-in. */
  preview?: React.ReactNode;
  /** The credits of the reel: 1 (`reelCredits`). */
  cost: number;
  canGenerate: boolean;
  isGenerating: boolean;
  /** True after a 503 `AI_REELS_UNAVAILABLE`: the red alert shows above Generate (design a6). */
  isUnavailable: boolean;
  onGenerate: () => void;
  /** The answer of a failed Generate: it shows under the button, not at the top of the page. */
  actionFeedback?: StudioActionFeedback | null;
}

/**
 * The right rail of the AI reel form in the Studio (design a1, a2, release-kit n4): a 9:16 stand-in, the choices, the
 * credit block (design release-kit t5), the Free watermark note and Generate. Below 700 px the
 * cost, the balance and Generate stick to the bottom of the page as one bar (t5b). A reel costs one
 * credit: when the balance (plan plus packs) is 0, Generate reads "Buy credits to make it" and
 * opens the credit packs at once (owner rule 2026-10-06).
 */
const ReelRailComponent: React.FC<ReelRailProps> = (props) => {
  const {
    accountId,
    releaseTitle,
    themeId,
    preview,
    cost,
    canGenerate,
    isGenerating,
    isUnavailable,
    onGenerate,
    actionFeedback = null,
  } = props;
  const intl = useIntl();
  const { clipWatermark } = useMusic24Capabilities();
  const gate = useVideoCreditGate(accountId);
  const isShort = gate.isShort(cost);
  const isNarrow = useIsNarrowCreditBar();

  // The button sits in the rail, or in the sticky bar below 700 px (design t5b).
  const generateButton = (isWide: boolean) => (
    <SMLButton
      color="primary"
      size={isWide ? 'lg' : 'md'}
      stretch={isWide}
      omitCap
      leftIcon={faSparkles}
      disabled={!canGenerate || isGenerating}
      isLoading={isGenerating}
      onClick={isShort ? gate.offerCredits : onGenerate}
    >
      {isShort ? gate.shortLabel : intl.formatMessage(reelMessages.generate)}
    </SMLButton>
  );

  const summary = useMemo(() => {
    const notSet = intl.formatMessage(reelMessages.railNotSet);
    const rows: Array<[string, string]> = [
      [intl.formatMessage(reelMessages.release), releaseTitle ?? notSet],
      [intl.formatMessage(reelMessages.theme), intl.formatMessage(releaseThemeMessages[themeId].name)],
      // Design z6: one length, no voiceover.
      [
        intl.formatMessage(reelMessages.length),
        intl.formatMessage(reelMessages.seconds, { count: AI_REEL_LENGTH_SEC }),
      ],
    ];
    return rows.map(([label, value]) => (
      <div key={label} className={styles.sumRow}>
        <span>{label}</span>
        <span className={styles.sumValue}>{value}</span>
      </div>
    ));
  }, [intl, releaseTitle, themeId]);

  return (
    <div className={styles.rail} data-testid="reel-rail">
      <Panel>
        <PanelHead
          title={intl.formatMessage(reelMessages.railTitle)}
          right={
            <span className={styles.railSize}>
              {intl.formatMessage(reelMessages.railSize, { width: 1080, height: 1920 })}
            </span>
          }
        />
        <div className={styles.railBody}>
          {preview ?? (
            <div className={styles.placeholder}>
              <span className={styles.placeholderText}>{intl.formatMessage(reelMessages.railPlaceholder)}</span>
            </div>
          )}
          {summary}
          {/* design t5: one credit block for every studio. */}
          {!isNarrow && (
            <StudioCreditBlock
              accountId={accountId}
              cost={cost}
              showShortNote={canGenerate}
              shortNoteTestId="reel-rail-short"
            />
          )}
          {/* design: trial-upsell a6. Before a render that costs credits, on Free and the trial. */}
          {clipWatermark && cost > 0 && <WatermarkCallout variant="make" surface="make_dialog" credits={cost} />}
          {isUnavailable && <ReelUnavailableAlert />}
          {isNarrow && <StudioActionError feedback={actionFeedback} />}
          {isNarrow ? (
            <StudioStickyMakeBar
              accountId={accountId}
              cost={cost}
              showShortNote={canGenerate}
              shortNoteTestId="reel-rail-short"
            >
              {generateButton(false)}
            </StudioStickyMakeBar>
          ) : (
            <>
              {generateButton(true)}
              <StudioActionError feedback={actionFeedback} />
            </>
          )}
          <p className={styles.tiny}>{intl.formatMessage(reelMessages.generateHint)}</p>
        </div>
      </Panel>
    </div>
  );
};

export const ReelRail = TypedMemo(ReelRailComponent);
